"use client";

import React from "react";
import { Award, Users, Shield, CheckCircle, Star } from "lucide-react";
import Image from "next/image";
import eeatsTrustIndicatorsData from "@data/components/eeats-trust-indicators.json";

interface TrustIndicator {
  icon: React.ReactNode;
  title: string;
  description: string;
  metric?: string;
}

interface EEATSTrustIndicatorsProps {
  variant?: "default" | "compact" | "detailed";
  className?: string;
}

// Icon mapping from JSON iconName to React component
const iconMap: Record<string, React.ComponentType<any>> = {
  Award,
  Users,
  Shield,
  CheckCircle,
  Star,
};

export const EEATSTrustIndicators: React.FC<EEATSTrustIndicatorsProps> = ({
  variant = "default",
  className = "",
}) => {
  // Load trust indicators from JSON and map icons
  const trustIndicators: TrustIndicator[] = eeatsTrustIndicatorsData.trustIndicators.map(
    (indicator) => {
      const IconComponent = iconMap[indicator.iconName] || Award;
      return {
        icon: <IconComponent className="w-6 h-6" />,
        title: indicator.title,
        description: indicator.description,
        metric: indicator.metric,
      };
    }
  );

  const clientLogos = eeatsTrustIndicatorsData.clientLogos;
  const testimonial = eeatsTrustIndicatorsData.testimonial;

  if (variant === "compact") {
    return (
      <div className={`bg-gray-50 rounded-xl p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-0">
            Vertrauensindikatoren
          </h3>
          <div className="flex items-center text-abis-accent">
            <Star className="w-5 h-5 mr-1" />
            <span className="text-sm font-medium">Expertenwissen</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {trustIndicators.slice(0, 4).map((indicator, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-16 h-16 bg-abis-primary/10 rounded-lg flex items-center justify-center text-abis-primary">
                {indicator.icon}
              </div>
              <div className="min-w-0 flex-1 mt-4">
                <p className="text-sm font-medium text-gray-900 truncate mb-0">
                  {indicator.title}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {indicator.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "detailed") {
    return (
      <div
        className={`bg-white border border-gray-200 rounded-2xl p-8 ${className}`}
      >
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Warum DBI Analytics vertrauen?
          </h2>
          <p className="text-gray-600">
            Expertise, Erfahrung, Autorität und Vertrauen - die Grundpfeiler
            unserer Arbeit
          </p>
        </div>

        {/* Trust Indicators Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {trustIndicators.map((indicator, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-abis-primary to-abis-primary/80 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                {indicator.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-0">
                {indicator.title}
              </h3>
              <p className="text-gray-600 text-sm mb-2">
                {indicator.description}
              </p>
              {indicator.metric && (
                <span className="inline-block bg-abis-accent/10 text-abis-accent px-3 py-1 rounded-full text-xs font-medium">
                  {indicator.metric}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Client Logos */}
        <div className="border-t border-gray-200 pt-8">
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-6">
            Vertrauen von führenden Unternehmen
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center justify-items-center opacity-60">
            {clientLogos.map((client, index) => (
              <div
                key={index}
                className="flex items-center justify-center h-12 w-full"
              >
                <div className="text-gray-400 font-semibold text-sm">
                  {client.name}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="border-t border-gray-200 pt-8 mt-8">
          <div className="bg-gradient-to-r from-abis-primary/5 to-abis-accent/5 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-abis-primary rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-lg">{testimonial.initials}</span>
                </div>
              </div>
              <div className="flex-1">
                <blockquote className="text-gray-700 italic mb-2">
                  "{testimonial.quote}"
                </blockquote>
                <footer className="text-sm text-gray-600">
                  <cite className="font-semibold not-italic">
                    {testimonial.author}
                  </cite>
                  <span className="block">{testimonial.role}</span>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div className={`bg-white rounded-xl shadow-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Expertenwissen</h3>
        <div className="flex items-center text-abis-accent">
          <Award className="w-5 h-5 mr-2" />
          <span className="text-sm font-medium">Zertifiziert</span>
        </div>
      </div>

      <div className="space-y-4">
        {trustIndicators.map((indicator, index) => (
          <div key={index} className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-abis-primary/10 rounded-lg flex items-center justify-center text-abis-primary">
              {indicator.icon}
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900">
                {indicator.title}
              </h4>
              <p className="text-sm text-gray-600">{indicator.description}</p>
              {indicator.metric && (
                <span className="inline-block bg-abis-accent/10 text-abis-accent px-2 py-1 rounded text-xs font-medium mt-1">
                  {indicator.metric}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Client Logos */}
      <div className="border-t border-gray-200 pt-6 mt-6">
        <p className="text-sm text-gray-600 text-center mb-4">
          Vertrauen von führenden Unternehmen
        </p>
        <div className="flex items-center justify-center space-x-6 opacity-60">
          {clientLogos.slice(0, 3).map((client, index) => (
            <div key={index} className="text-gray-400 font-semibold text-sm">
              {client.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EEATSTrustIndicators;
